import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/auth/AuthContext";
import LandLordContext from "../../context/landlord/LandLordContext";
import {
  RowButton,
  Button,
  Icon,
  SkeletonList,
  Tabs,
  TextField,
  WorkspaceCard,
} from "../design";
import {
  formatPropertyAddress,
  getNotificationDisplayText,
  getUserDisplayName,
  haveValue,
  placeNotificationLink,
  shouldDeductNextView,
  toast,
} from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import ServingPromptModal from "../../pages/landlord/profile-request/ServingPromptModal";

const SEARCH_TYPES = {
  TENANTS: "tenants",
  APPLICATIONS: "applications",
  PROPERTIES: "properties",
  NOTIFICATIONS: "notifications",
};

const normalizeText = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const idValue = (value) =>
  value?._id?.toString?.() || value?.id?.toString?.() || value?.toString?.();

const getResponseRows = (response, type) => {
  const payload = response?.success_res ?? response?.data ?? response ?? {};

  if (Array.isArray(payload)) return payload;

  return (
    payload?.results ?? payload?.data ?? payload?.[type] ?? payload?.items ?? []
  );
};

const getResponsePagination = (response) => {
  const payload = response?.success_res ?? response?.data ?? response ?? {};
  return payload?.pagination ?? {};
};

const GlobalWorkspaceSearch = ({ workspaces = [] }) => {
  const { _t, i18n } = useTranslator();
  const navigate = useNavigate();
  const {
    user,
    notificationSeen,
    setLoading: setAuthLoading,
  } = useContext(AuthContext);
  const { fetchGlobalWorkspaceSearch } = useContext(LandLordContext);
  const [activeType, setActiveType] = useState(SEARCH_TYPES.TENANTS);
  const [query, setQuery] = useState("");
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [creditPrompt, setCreditPrompt] = useState(null);
  const loadingMoreRef = useRef(false);
  const latestSearchRef = useRef("");
  const isSpanish = i18n.language?.toLowerCase().startsWith("es");
  const searchCopy = {
    [SEARCH_TYPES.TENANTS]: {
      label: _t("searchTenant"),
      placeholder: _t("globalSearchTenantsPlaceholder"),
    },
    [SEARCH_TYPES.PROPERTIES]: {
      label: _t("searchProperty"),
      placeholder: _t("globalSearchPropertyPlaceholder"),
    },
    [SEARCH_TYPES.NOTIFICATIONS]: {
      label: _t("searchNotification"),
      placeholder: _t("globalSearchNotificationPlaceholder"),
    },
  };

  const membershipMap = useMemo(() => {
    const map = new Map();
    const merged = [...(user?.workspaceIds ?? []), ...(workspaces ?? [])];

    merged.forEach((workspace) => {
      const workspaceData = workspace?.workspaceId ?? workspace;
      const combined = { ...workspaceData, ...workspace };
      [workspaceData?._id, workspaceData?.uuid, workspace?._id, workspace?.uuid]
        .filter(haveValue)
        .forEach((key) => map.set(key.toString(), combined));
    });

    return map;
  }, [user?.workspaceIds, workspaces]);

  const resolveWorkspace = useCallback(
    (workspaceValue, row) => {
      const workspace =
        workspaceValue?.workspaceId ??
        workspaceValue?.workspace ??
        workspaceValue ??
        row?.workspaceId ??
        row?.workspace;
      const workspaceObject =
        typeof workspace === "object" && workspace !== null ? workspace : {};
      const possibleIds = [
        workspaceObject?._id,
        workspaceObject?.uuid,
        workspace,
        row?.workspace_id,
        row?.workspace_uuid,
      ]
        .filter(haveValue)
        .map((value) => value.toString());
      const membership = possibleIds
        .map((key) => membershipMap.get(key))
        .find(Boolean);

      if (!membership) return null;

      return {
        ...workspaceObject,
        ...membership,
        _id: membership?._id ?? workspaceObject?._id,
        uuid: membership?.uuid ?? workspaceObject?.uuid,
        name: membership?.name ?? workspaceObject?.name,
      };
    },
    [membershipMap]
  );

  const results = useMemo(() => {
    const grouped = new Map();

    rawRows.forEach((row) => {
      if (activeType === SEARCH_TYPES.NOTIFICATIONS) {
        const workspace = resolveWorkspace(row?.workspaceId, row);
        const notificationText = getNotificationDisplayText(row, _t);

        if (workspace?.uuid) {
          grouped.set(idValue(row) || `${row?.createdAt}-${row?.body}`, {
            ...row,
            key: idValue(row) || `${row?.createdAt}-${row?.body}`,
            type: activeType,
            name: notificationText,
            workspaces: [workspace],
          });
        }
        return;
      }

      if (row?.result_type === SEARCH_TYPES.APPLICATIONS) {
        const application = row?.application ?? row?.application_id ?? row;
        const name = row?.title ?? application?.title ?? "";
        const applicationId =
          idValue(application) ||
          idValue(row?.applicationId) ||
          normalizeText(name);
        const current = grouped.get(applicationId) ?? {
          key: applicationId,
          type: SEARCH_TYPES.APPLICATIONS,
          applicationId,
          name,
          participantCount:
            row?.participant_count ?? application?.participants?.length ?? 0,
          workspaces: [],
        };
        const workspaceValues =
          Array.isArray(row?.workspaces) && row.workspaces.length
            ? row.workspaces
            : [row?.workspace ?? row?.workspaceId ?? row];

        workspaceValues.forEach((workspaceValue) => {
          const workspace = resolveWorkspace(workspaceValue, row);
          if (
            workspace?.uuid &&
            !current.workspaces.some(
              (item) => item.uuid?.toString() === workspace.uuid?.toString()
            )
          ) {
            current.workspaces.push(workspace);
          }
        });

        if (current.workspaces.length) grouped.set(applicationId, current);
        return;
      }

      const entity =
        activeType === SEARCH_TYPES.TENANTS
          ? (row?.tenant ?? row?.tenant_id ?? row?.user ?? row)
          : (row?.property ?? row?.property_id ?? row);
      const workspaceValues =
        Array.isArray(row?.workspaces) && row.workspaces.length
          ? row.workspaces
          : [row?.workspace ?? row?.workspaceId ?? row];

      if (activeType === SEARCH_TYPES.TENANTS) {
        const name =
          row?.name ??
          getUserDisplayName(entity, "") ??
          [entity?.tenant_fname, entity?.tenant_lname]
            .filter(haveValue)
            .join(" ");
        const email = row?.email ?? entity?.email ?? entity?.tenant_email ?? "";
        const tenantId =
          idValue(entity) || idValue(row?.tenantId) || normalizeText(email);
        const key =
          tenantId || `${normalizeText(name)}|${normalizeText(email)}`;
        const current = grouped.get(key) ?? {
          key,
          type: activeType,
          tenantId,
          name,
          email,
          workspaces: [],
        };

        workspaceValues.forEach((workspaceValue) => {
          const workspace = resolveWorkspace(workspaceValue, row);
          const sharedProfileId =
            idValue(workspaceValue?.sharedProfileId) ??
            idValue(workspaceValue?.shared_profile_id) ??
            idValue(workspaceValue?.profileId) ??
            idValue(row?.sharedProfileId) ??
            idValue(row?.shared_profile_id) ??
            idValue(row?.profileId) ??
            (row?.tenant_id ? idValue(row) : undefined);

          if (
            workspace?.uuid &&
            !current.workspaces.some(
              (item) => item.uuid?.toString() === workspace.uuid?.toString()
            )
          ) {
            current.workspaces.push({
              ...workspace,
              targetId: sharedProfileId,
            });
          }
        });

        if (current.workspaces.length) grouped.set(key, current);
        return;
      }

      const address = formatPropertyAddress(entity, i18n.language);
      const postcode =
        entity?.postcode ?? entity?.postalCode ?? entity?.postal_code ?? "";
      const key = `${normalizeText(address)}|${normalizeText(postcode)}`;
      const current = grouped.get(key) ?? {
        key,
        type: activeType,
        address,
        postcode,
        workspaces: [],
      };

      workspaceValues.forEach((workspaceValue) => {
        const workspace = resolveWorkspace(workspaceValue, row);
        const propertyId =
          idValue(workspaceValue?.propertyId) ??
          idValue(workspaceValue?.property_id) ??
          idValue(row?.propertyId) ??
          idValue(row?.property_id) ??
          idValue(entity);

        if (
          workspace?.uuid &&
          propertyId &&
          !current.workspaces.some(
            (item) => item.uuid?.toString() === workspace.uuid?.toString()
          )
        ) {
          current.workspaces.push({ ...workspace, targetId: propertyId });
        }
      });

      if (current.workspaces.length) grouped.set(key, current);
    });

    return Array.from(grouped.values());
  }, [activeType, i18n.language, rawRows, resolveWorkspace, _t]);

  useEffect(() => {
    const trimmedQuery = query.trim();
    const searchKey = `${activeType}|${trimmedQuery}`;
    latestSearchRef.current = searchKey;

    if (!trimmedQuery) {
      setRawRows([]);
      setLoading(false);
      setLoadingMore(false);
      setHasNextPage(false);
      setPage(1);
      loadingMoreRef.current = false;
      return undefined;
    }

    setLoading(true);
    setRawRows([]);
    setHasNextPage(false);
    setPage(1);
    let ignore = false;
    const timer = setTimeout(async () => {
      try {
        const response = await fetchGlobalWorkspaceSearch({
          query: trimmedQuery,
          type: activeType,
          page: 1,
        });
        if (!ignore && latestSearchRef.current === searchKey) {
          const pagination = getResponsePagination(response);
          setRawRows(getResponseRows(response, activeType));
          setHasNextPage(Boolean(pagination?.has_next_page));
        }
      } catch (error) {
        if (!ignore) setRawRows([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    }, 250);

    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [activeType, fetchGlobalWorkspaceSearch, query]);

  const handleQueryChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setRawRows([]);
    setHasNextPage(false);
    setPage(1);
    setLoading(Boolean(value.trim()));
  };

  const changeSearchType = (type) => {
    if (type === activeType) return;
    setActiveType(type);
    setRawRows([]);
    setHasNextPage(false);
    setPage(1);
    setLoading(Boolean(query.trim()));
  };

  const loadNextPage = async () => {
    if (!query.trim() || loading || loadingMoreRef.current || !hasNextPage) {
      return;
    }

    const nextPage = page + 1;
    const searchKey = `${activeType}|${query.trim()}`;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const response = await fetchGlobalWorkspaceSearch({
        query: query.trim(),
        type: activeType,
        page: nextPage,
      });

      if (latestSearchRef.current === searchKey) {
        const pagination = getResponsePagination(response);
        setRawRows((previous) => [
          ...previous,
          ...getResponseRows(response, activeType),
        ]);
        setPage(nextPage);
        setHasNextPage(Boolean(pagination?.has_next_page));
      }
    } catch (error) {
      // Keep the already loaded pages visible if loading the next page fails.
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const handleResultsScroll = (event) => {
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop - element.clientHeight <= 80) {
      loadNextPage();
    }
  };

  const openResult = async (row, workspace) => {
    if (row.type === SEARCH_TYPES.NOTIFICATIONS) {
      const [notificationWorkspace] = row.workspaces;
      if (row?.shared_profile_id?.pendingDecision === true) {
        toast.error(_t("pendingDecisionFromTenant"));
        return;
      }
      notificationSeen(row?._id);
      navigate(placeNotificationLink(row, notificationWorkspace?.uuid));
      return;
    }

    if (!workspace) {
      if (row.workspaces.length > 1) {
        setSelectedRow(row);
        return;
      }
      [workspace] = row.workspaces;
    }

    setSelectedRow(null);
    if (row.type === SEARCH_TYPES.APPLICATIONS) {
      navigate(
        `/${workspace.uuid}/profile-request?search=${encodeURIComponent(
          row.name || query.trim()
        )}`
      );
      return;
    }

    const targetId = workspace.targetId;

    if (!targetId) {
      toast.error(_t("failedToGetProfile"));
      return;
    }

    if (row.type === SEARCH_TYPES.TENANTS) {
      const destination = `/${workspace.uuid}/profile-property?sharedprofileid=${targetId}`;
      setAuthLoading(true);
      try {
        const shouldDeduct = await shouldDeductNextView(
          user,
          targetId,
          row.tenantId,
          workspace.uuid
        );
        if (shouldDeduct) {
          setCreditPrompt({
            URL: destination,
            profile: { tenantid: { email: row.email } },
          });
        } else {
          navigate(destination);
        }
      } finally {
        setAuthLoading(false);
      }
    } else {
      navigate(
        `/${workspace.uuid}/home?propertyId=${targetId}&propName=${encodeURIComponent(
          row.address
        )}`
      );
    }
  };

  return (
    <>
      <section
        className={`all-workspace-container global-workspace-search ${
          query.trim() ? "has-results" : ""
        }`}
        aria-label={_t("globalSearch")}
      >
        <Tabs
          className="global-search-tabs"
          label={_t("globalSearch")}
          value={activeType}
          onChange={changeSearchType}
          items={[
            {
              value: SEARCH_TYPES.TENANTS,
              label: _t("tenants"),
              icon: <Icon name="user" size={16} />,
            },
            {
              value: SEARCH_TYPES.PROPERTIES,
              label: _t("properties"),
              icon: <Icon name="home" size={16} />,
            },
            {
              value: SEARCH_TYPES.NOTIFICATIONS,
              label: _t("notifications"),
              icon: <Icon name="bell" size={16} />,
            },
          ]}
        />

        <TextField
          id="global-workspace-search"
          className="global-search-field"
          label={searchCopy[activeType].label}
          icon={<Icon name="search" />}
          value={query}
          onChange={handleQueryChange}
          placeholder={searchCopy[activeType].placeholder}
          trailing={
            query && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={_t("clearSearch")}
                onClick={() => {
                  setQuery("");
                  setRawRows([]);
                }}
              >
                <Icon name="close" size={16} />
              </Button>
            )
          }
        />

        {query.trim() && (
          <div
            className="global-search-results"
            aria-live="polite"
            onScroll={handleResultsScroll}
          >
            {loading ? (
              <SkeletonList rows={3} avatarSize={36} label={_t("loading")} />
            ) : results.length ? (
              results.map((row) => (
                <RowButton
                  type="button"
                  className="global-search-row"
                  key={row.key}
                  onClick={() => openResult(row)}
                >
                  <span
                    className={`global-search-row-icon ${
                      row.type === SEARCH_TYPES.PROPERTIES ? "property" : ""
                    }`}
                  >
                    {row.type === SEARCH_TYPES.TENANTS ? (
                      (row.name || row.email)
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join("")
                        .toUpperCase()
                    ) : row.type === SEARCH_TYPES.PROPERTIES ? (
                      <img src="/images/home-icon-green.svg" alt="" />
                    ) : row.type === SEARCH_TYPES.APPLICATIONS ? (
                      <img src="/images/multiple-user.svg" alt="" />
                    ) : (
                      <img src="/images/bell-icon-black.svg" alt="" />
                    )}
                  </span>
                  <span className="global-search-row-copy">
                    <strong>
                      {row.type === SEARCH_TYPES.PROPERTIES
                        ? row.address
                        : row.name}
                    </strong>
                    {row.type === SEARCH_TYPES.TENANTS && (
                      <small>{row.email}</small>
                    )}
                    {row.type === SEARCH_TYPES.APPLICATIONS && (
                      <small>
                        {row.participantCount} {_t("tenants")}
                      </small>
                    )}
                  </span>
                  <span className="global-search-workspaces">
                    {row.workspaces.map((workspace) => (
                      <span key={workspace.uuid}>{workspace.name}</span>
                    ))}
                  </span>
                  {row.type !== SEARCH_TYPES.NOTIFICATIONS &&
                    row.workspaces.length > 1 && (
                      <img
                        className="global-search-chevron"
                        src="/images/arrow-right-gray.svg"
                        alt=""
                      />
                    )}
                </RowButton>
              ))
            ) : (
              <div className="global-search-state">{_t("noDataFound")}</div>
            )}
            {loadingMore && (
              <SkeletonList rows={1} avatarSize={36} label={_t("loading")} />
            )}
          </div>
        )}
      </section>

      <Modal
        show={Boolean(selectedRow)}
        onHide={() => setSelectedRow(null)}
        centered
        className="newmodalui tailwind-base-modal wrapper-modal global-workspace-selector"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isSpanish ? "Elige un espacio de trabajo" : "Choose a workspace"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRow?.workspaces.map((workspace) => (
            <WorkspaceCard
              key={workspace.uuid}
              name={workspace.name}
              lines={[
                haveValue(workspace.memberCount) &&
                  `${workspace.memberCount} ${_t("members")}`,
              ]}
              onClick={() => openResult(selectedRow, workspace)}
            />
          ))}
        </Modal.Body>
      </Modal>

      <ServingPromptModal
        openModal={Boolean(creditPrompt)}
        profile={creditPrompt?.profile}
        URL={creditPrompt?.URL}
        setOpenModal={(open) => {
          if (!open) setCreditPrompt(null);
        }}
      />
    </>
  );
};

export default GlobalWorkspaceSearch;
